import { sortBy, reverse, chain } from 'lodash';
import axios from 'axios';

const { VUE_APP_BASE_URL } = process.env;

export const objectImageset = ({
  projectId,
  visitId,
  objection
}) => axios.post(`/projects/${projectId}/visits/${visitId}/objections`, { objection });

export const fetchSkuPenetration = (projectId, params) => axios.get(`/projects/${projectId}/sku_aggregations/penetration`, { params });
export const fetchSkuPenetrationByType = (projectId, params) => axios.get(`/projects/${projectId}/sku_aggregations/penetration/by_types`, { params });

export const approveVisitValidation = (projectId, visitId, skuInspectionId) => axios.post(`/projects/${projectId}/visits/${visitId}/sku_validations/${skuInspectionId}/approve_sku_validation`);
export const downloadScene = (projectId, imagesetId, sceneType) => axios.get(`/projects/${projectId}/image_sets/${imagesetId}/download_scene?scene_type=${sceneType}`);
export const fetchSku = (projectId, sku) => axios.get(`/projects/${projectId}/skus/${sku}`);

export const fetchSkus = (projectId, params) => axios.post(`/projects/${projectId}/skus`, params);
export const fetchSuggestions = (projectId, scope, params) => axios.get(`/projects/${projectId}/${scope}/suggestions`, { params });
export const fetchTeam = (projectId, teamId, params) => axios.get(`/projects/${projectId}/teams/${teamId}`, { params });
export const fetchTeamGroup = (projectId, teamGroupId, params) => axios.get(`/projects/${projectId}/team_groups/${teamGroupId}`, { params });
export const fetchTeamGroups = (projectId, params) => axios.get(`/projects/${projectId}/team_groups`, { params });
export const fetchVisits = (projectId, params) => axios.get(`/projects/${projectId}/visits`, { params });
export const postComment = (projectId, visitId, objectionId, body) => axios.post(`projects/${projectId}/visits/${visitId}/objections/${objectionId}/threads`, body);
export const postObjection = (projectId, visitId, objection) => axios.post(`/projects/${projectId}/visits/${visitId}/objections`, { objection });
export const fetchSkuCandidatePool = projectId => axios.get(`/projects/${projectId}/sku_candidates`);
export const postSkuCandidate = (projectId, body) => axios.post(`/projects/${projectId}/sku_candidates`, body);
export const discardSkuCandidate = (projectId, id) => axios.delete(`/projects/${projectId}/sku_candidates/${id}`);
export const proposeForEnrollment = (projectId, body) => axios.post(`/projects/${projectId}/sku_candidates/bulk`, body);

export const candidateBulkCreate = (projectId, body) => axios.post(`/projects/${projectId}/sku_candidates/bulk_create`, body);
export const candidateBulkDelete = (projectId, body) => axios.delete(`/projects/${projectId}/sku_candidates/bulk_delete`, body);

export const fetchSkuFacingShare = async (projectId, params) => {
  const { data: { data, meta } } = await axios({
    url: `/projects/${projectId}/sku/facing_share`,
    baseURL: `${VUE_APP_BASE_URL}/api/ocean/v2/`,
    params
  });

  return Promise.resolve({ data: { data, meta } });
};

export const fetchSkuPenetrationList = async (projectId, params) => {
  const { data: { data: sku_penetartions, meta: sku_penetartion_meta } } = await fetchSkuPenetration(projectId, params);
  const { data: { data: sku_list } } = await fetchSkus(projectId, { per: 5000 }); // Assuming that there is no more than 5000 skus
  const { data: { data: imageset_penetrations } } = await fetchSkuPenetrationByType(projectId, params);

  const imagesets = imageset_penetrations.reduce((acc, penetrations_by_type) => {
    acc.push(...Object.keys(penetrations_by_type));

    return acc;
  }, []);

  let merged_sku_list = sku_list.map((sku) => {
    const found_sku = sku_penetartions.find(p => p.id === sku.id);

    return {
      ...sku,
      shelf_spaces: found_sku ? parseFloat(found_sku.shelf_spaces) : 0,
      realogram_count: found_sku ? found_sku.realogram_count : 0,
      penetration: found_sku ? found_sku.penetration : 0
    };
  });

  merged_sku_list = merged_sku_list.map((sku) => {
    const imagesets_penetrations = imagesets.reduce((acc, imageset_name) => {
      const imageset_penetration = imageset_penetrations.find(penetrations_by_type => penetrations_by_type[imageset_name]);
      const imageset_penetration_sku = imageset_penetration[imageset_name].data.find(item => item.id === sku.id);

      acc[imageset_name] = {
        shelf_spaces: imageset_penetration_sku ? parseFloat(imageset_penetration_sku.shelf_spaces) : 0,
        realogram_count: imageset_penetration_sku ? imageset_penetration_sku.realogram_count : 0,
        penetration: imageset_penetration_sku ? imageset_penetration_sku.penetration : 0
      };

      return acc;
    }, {});

    return {
      ...sku,
      ...imagesets_penetrations
    };
  });

  merged_sku_list = reverse(sortBy(merged_sku_list, 'penetration'));

  const meta = { imagesets, ...sku_penetartion_meta };

  return Promise.resolve({ data: merged_sku_list, meta });
};

export const fetchSkuPenetrationListWithoutTypes = async (projectId, params) => {
  const { data: { data: sku_penetrations, meta: sku_penetration_meta } } = await fetchSkuPenetration(projectId, params);
  const { data: { data: sku_list } } = await fetchSkus(projectId, { per: 5000 }); // Assuming that there is no more than 5000 skus

  let merged_sku_list = sku_list.map((sku) => {
    const found_sku = sku_penetrations.find(p => p.id === sku.id);

    return {
      ...sku,
      shelf_spaces: found_sku ? parseFloat(found_sku.shelf_spaces) : 0,
      realogram_count: found_sku ? found_sku.realogram_count : 0,
      penetration: found_sku ? found_sku.penetration : 0
    };
  });

  merged_sku_list = merged_sku_list.map(sku => ({
    ...sku
  }));

  merged_sku_list = reverse(sortBy(merged_sku_list, 'penetration'));

  const meta = { ...sku_penetration_meta };

  return Promise.resolve({ data: merged_sku_list, meta });
};

export const fetchSkuAvailability = async (projectId, listName = '', params) => {
  if (listName !== '') {
    // eslint-disable-next-line no-param-reassign
    params.list = listName;
  }

  const { data: { data } } = await axios({
    url: `/projects/${projectId}/sku/availability`,
    baseURL: `${VUE_APP_BASE_URL}/api/ocean/v2/`,
    params
  });

  const isSkuList = chain(data)
    .head()
    .get('key')
    .isSafeInteger()
    .value();

  let list = data;

  if (isSkuList) {
    const { data: { data: allSkus } } = await fetchSkus(projectId, { per: 10000 });

    list = list.map(item => ({
      ...item,
      sku: allSkus.find(sku => sku.id === item.key)
    }));
  }

  return Promise.resolve({ data: { data: list } });
};

export const downloadFile = (url, title) => new Promise((resolve, reject) => {
  fetch(url)
    .then(response => response.arrayBuffer())
    .then((data) => {
      const file = new Blob([data], { type: 'application/json' });
      const fileURL = URL.createObjectURL(file);
      const link = document.createElement('a');
      link.href = fileURL;
      link.target = '_blank';
      link.download = title;
      link.click();

      return resolve();
    }).catch(() => reject());
});

export const fetchSkuAvailabilityList = async (projectId, params) => {
  const data = await axios.get(`/projects/${projectId}/sku_availability`, { params });

  return Promise.resolve(data);
};

export const exportSkuAvailabilityList = (projectId, params) => {
  const data = axios.get(`/projects/${projectId}/sku_availability/export`, { params });

  return Promise.resolve(data);
};

export const fetchSkusWithoutInterceptor = async (projectId, params) => {
  const axiosWithoutInterceptors = axios.create({
    baseURL: VUE_APP_BASE_URL
  });

  const { data: { data } } = await axiosWithoutInterceptors
    .post(`/api/ocean/v1/projects/${projectId}/skus`, params);

  return Promise.resolve(data);
};

export const userTimeSpend = async (params) => {
  await axios.put('/spend', params);
};

export const fetchImagesetTypes = projectId => axios.get(`/projects/${projectId}/image_set_types`);
export const fetchActivityCategories = projectId => axios.get(`/projects/${projectId}/activity_categories`);
export const fetchODRReports = (projectId, params) => axios.get(`/projects/${projectId}/odr_reports`, { params });

export default {
  downloadScene,
  fetchSku,
  fetchSkuPenetration,
  fetchSkuPenetrationByType,
  fetchSkuPenetrationList,
  fetchSkus,
  fetchSkuAvailability,
  fetchSuggestions,
  fetchTeam,
  fetchTeamGroup,
  fetchTeamGroups,
  fetchVisits,
  objectImageset,
  postComment,
  postObjection,
  fetchSkuAvailabilityList,
  exportSkuAvailabilityList,
  fetchSkuFacingShare,
  fetchSkusWithoutInterceptor,
  fetchImagesetTypes,
  fetchActivityCategories,
  fetchODRReports
};
